import { Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { TelephonyPluginConfigService } from '../telephony/telephony-plugin-config.service';

// ── Types ─────────────────────────────────────────────────────
export type SipStatus   = 'idle' | 'connecting' | 'registered' | 'failed';
export type CallStatus  = 'idle' | 'ringing' | 'active' | 'on-hold' | 'reconnecting' | 'ended';
export type CaptionStatus = 'off' | 'starting' | 'live' | 'stopped' | 'failed';

export interface CallerInfo  { name: string; phone: string; cnic: string; city: string; channel: string; callerLanguage?: string; ivrOption?: string; ivrOptionLabel?: string; }
export interface AgentOption { name: string; ext: string; status: 'Available' | 'Away' | 'On Call'; color: string; }
export interface TranscriptMsg { type: 'caller' | 'citizen' | 'agent' | 'system'; initials: string; text: string; time: string; }
export interface CaptionSegment { sequenceNo?: number; speakerLabel?: string; text: string; isFinal?: boolean; startMs?: number; endMs?: number; }
export interface WrapUpDetails {
  notes: string;
  disposition?: string;
  callReason?: string;
  sourceOfInformation?: string;
  callbackRequired?: boolean;
  callbackDueAt?: string;
  callbackStatus?: string;
  holdDurationSeconds?: number;
}
export interface ConferenceParticipant { ext: string; name: string; role: 'CITIZEN' | 'PRIMARY_AGENT' | 'CONFERENCE_AGENT'; }
export interface IncomingConferenceInfo { channelId: string; callerNumber: string; initiatorExt: string; initiatorName: string; }

// ── Private internal types ─────────────────────────────────────
type JsSIPSession = any;
type JsSIPUA      = any;
type StompClient  = any;
type SupervisorAssistKind = 'monitor' | 'coach' | '';

interface CaptionCapture {
  recorder: MediaRecorder | null;
  mediaStream: MediaStream;
  mimeType?: string;
  chunkTimer?: ReturnType<typeof setTimeout>;
  streams: MediaStream[];
  audioContext: AudioContext;
  sources: MediaStreamAudioSourceNode[];
  connectedTrackIds: Set<string>;
  remoteAttachTimer?: ReturnType<typeof setInterval>;
  sequenceNo: number;
  startedAt: number;
}

interface RecordingCapture {
  recorder: MediaRecorder;
  mimeType?: string;
  chunks: Blob[];
  streams: MediaStream[];
  audioContext: AudioContext;
  sources: MediaStreamAudioSourceNode[];
  connectedTrackIds: Set<string>;
  attachTimer?: ReturnType<typeof setInterval>;
  startedAt: number;
  sideCaptures: SideRecordingCapture[];
}

interface SideRecordingCapture {
  label: string;
  recorder: MediaRecorder;
  mimeType?: string;
  chunks: Blob[];
  streams: MediaStream[];
  audioContext: AudioContext;
  sources: MediaStreamAudioSourceNode[];
  connectedTrackIds: Set<string>;
  attachTimer?: ReturnType<typeof setInterval>;
  stopPromise: Promise<SideRecordingBlob>;
  resolveStop: (value: SideRecordingBlob) => void;
}

interface SideRecordingBlob {
  label: string;
  blob: Blob;
}

interface TranscriptSideSegment extends CaptionSegment {
  speakerLabel: string;
  sourceIndex: number;
}

interface RingToneHandle {
  context: AudioContext;
  gain: GainNode;
  interval: ReturnType<typeof setInterval> | null;
  stopTone: (() => void) | null;
}

// ── Config ─────────────────────────────────────────────────────
const SIP_WS_URL  = environment.sipWsUrl;
const SIP_DOMAIN  = environment.sipDomain;
const STOMP_URL   = environment.stompUrl;
const API_URL     = environment.apiHost;
const STUN_URL    = environment.stunUrl;
const TURN_TCP    = environment.turnTcpUrl;
const TURN_USER   = environment.turnUsername;
const TURN_CRED   = environment.turnCredential;
const TW_URL      = environment.transcriptionWorkerUrl;
const TW_LANG     = environment.transcriptionLanguage;
const CAPTION_CHUNK_MS = 30000;
const RECORDING_AUDIO_WAIT_MS = 3500;

const ICE_SERVERS: RTCIceServer[] = [
  ...(STUN_URL ? [{ urls: STUN_URL }] : []),
  ...(TURN_TCP ? [{ urls: TURN_TCP, username: TURN_USER, credential: TURN_CRED }] : []),
];
const PC_CONFIG: RTCConfiguration = ICE_SERVERS.length ? { iceServers: ICE_SERVERS, iceTransportPolicy: 'all' } : { iceServers: [] };

interface AgentAvailabilityDto {
  userId: number;
  fullName: string;
  sipExtension: string;
  status: 'AVAILABLE' | 'ON_CALL' | 'OFFLINE';
}

interface ExtensionPoolItem {
  extensionNumber: string;
  status: 'AVAILABLE' | 'RESERVED';
  reservedByMe: boolean;
  reservedByUserName: string | null;
}

export interface DirectoryExtensionItem {
  extensionNumber: string;
  pool: string;        // "CSR" | "INTERNAL_USER" | "SUPERVISOR" | "ADMIN" | "MANAGER"
  poolLabel: string;   // "CSR" | "Supervisor" | "Admin"
  status: 'AVAILABLE' | 'RESERVED';
  reservedByUserName: string | null;
}

// ── Remote audio module-level state ───────────────────────────
let csrRemoteAudioContext: AudioContext | null = null;
let csrRemoteAudioSource: MediaStreamAudioSourceNode | null = null;
let csrRemoteAudioStreamId = '';

// ── Helpers ────────────────────────────────────────────────────
function nowTime() { return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
function stompBrokerUrl(url: string) { const w = url.replace(/^http/, 'ws').replace(/\/+$/, ''); return w.endsWith('/websocket') ? w : `${w}/websocket`; }

function createRingToneHandle(): RingToneHandle | null {
  try {
    const Ctor = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return null;
    const context = new Ctor();
    const gain = context.createGain();
    gain.gain.value = 0.55;
    gain.connect(context.destination);
    return { context, gain, interval: null, stopTone: null };
  } catch { return null; }
}

function stopRingToneHandle(h: RingToneHandle | null) {
  if (!h) return;
  if (h.interval) { clearInterval(h.interval); h.interval = null; }
  h.stopTone?.(); h.stopTone = null;
}

function playRingToneHandle(h: RingToneHandle | null) {
  if (!h || h.interval) return;
  if (h.context.state === 'suspended') void h.context.resume().catch(() => undefined);
  const beep = () => {
    h.stopTone?.(); h.stopTone = null;
    const oscs: OscillatorNode[] = []; const gains: GainNode[] = [];
    const tone = (freq: number, offset: number) => {
      const o = h.context.createOscillator(); const g = h.context.createGain();
      o.type = 'square'; o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, h.context.currentTime + offset);
      g.gain.exponentialRampToValueAtTime(0.5, h.context.currentTime + offset + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, h.context.currentTime + offset + 0.38);
      o.connect(g); g.connect(h.gain);
      o.start(h.context.currentTime + offset); o.stop(h.context.currentTime + offset + 0.42);
      oscs.push(o); gains.push(g);
    };
    tone(440, 0); tone(520, 0.45);
    h.stopTone = () => { oscs.forEach(o => { try { o.stop(); } catch {} o.disconnect(); }); gains.forEach(g => g.disconnect()); };
  };
  beep(); h.interval = setInterval(beep, 1400);
}

// SDP munging ──────────────────────────────────────────────────
function createSdpMunger() {
  const portMap = new Map<string, string>();
  let preferred = '';
  const isIpv4 = (s: string) => /^\d+\.\d+\.\d+\.\d+$/.test(s);

  function mungeSdp(sdp: string): string {
    return sdp.split(/\r\n|\n/).flatMap(line => {
      if (line.startsWith('c=IN IP4 ') && preferred) return [`c=IN IP4 ${preferred}`];
      if (!line.startsWith('a=candidate:')) return [line];
      const parts = line.slice('a=candidate:'.length).split(' ');
      const proto = parts[2]?.toLowerCase(); const addr = parts[4] ?? ''; const port = parts[5] ?? '';
      if (proto !== 'udp') return [];
      if (addr.includes(':')) return [];
      if (addr.endsWith('.local')) {
        const real = portMap.get(port) || preferred;
        if (!real || !isIpv4(real)) return [];
        parts[4] = real; if (!preferred) preferred = real;
        return [`a=candidate:${parts.join(' ')}`];
      }
      if (!isIpv4(addr)) return [];
      if (!preferred) preferred = addr;
      return [line];
    }).join('\r\n');
  }

  return {
    rememberCandidate(c: RTCIceCandidate) {
      const a = c.address ?? ''; const p = c.protocol?.toLowerCase() ?? '';
      if (p === 'udp' && c.port && isIpv4(a)) { portMap.set(String(c.port), a); if (!preferred) preferred = a; }
    },
    shouldSignalReady(c: RTCIceCandidate) {
      const a = c.address ?? ''; const p = c.protocol?.toLowerCase() ?? '';
      const t = c.type ?? '';
      if (p !== 'udp') return false;
      if (!['host', 'srflx', 'relay'].includes(t)) return false;
      if (!isIpv4(a)) return false;
      if (!preferred) preferred = a;
      return true;
    },
    munge(ev: { originator: string; sdp: string }) {
      if (ev.originator !== 'local') return;
      ev.sdp = mungeSdp(ev.sdp);
    },
    mungeSdp,
  };
}

function attachRemoteAudio(pc: RTCPeerConnection, addSystem: (t: string) => void) {
  if ((pc as any).__grmsRemoteAudioAttached) return;
  (pc as any).__grmsRemoteAudioAttached = true;
  const attached = new Set<string>();

  const attachStream = (stream?: MediaStream) => {
    if (!stream) return;
    const newTracks = stream.getAudioTracks().filter(t => !attached.has(t.id));
    if (!newTracks.length) return;
    newTracks.forEach(t => attached.add(t.id));
    // The CSR console template provides this element; on other pages (e.g. the
    // supervisor control room during monitor/coach) it does not exist — create it,
    // otherwise incoming call audio is received but never played.
    let el = document.getElementById('remote-audio') as HTMLAudioElement | null;
    if (!el) {
      el = document.createElement('audio');
      el.id = 'remote-audio';
      el.style.display = 'none';
      document.body.appendChild(el);
      addSystem('Remote audio element created dynamically.');
    }
    el.autoplay = true; el.muted = false; el.volume = 1; el.setAttribute('playsinline', 'true');
    if (el.srcObject !== stream) el.srcObject = stream;
    el.play().catch(() => addSystem('Remote audio blocked; click inside page once.'));
    if (csrRemoteAudioStreamId !== stream.id) {
      try {
        try { csrRemoteAudioSource?.disconnect(); } catch {}
        const Ctor = window.AudioContext || (window as any).webkitAudioContext; if (!Ctor) return;
        const ctx = csrRemoteAudioContext && csrRemoteAudioContext.state !== 'closed' ? csrRemoteAudioContext : new Ctor();
        csrRemoteAudioContext = ctx;
        if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
        const src = ctx.createMediaStreamSource(stream);
        src.connect(ctx.destination);
        csrRemoteAudioSource = src; csrRemoteAudioStreamId = stream.id;
        addSystem('Remote audio playback attached.');
      } catch (e) { console.warn('[AUDIO] Web Audio failed', e); }
    }
  };

  pc.getReceivers().map(r => r.track).filter((t): t is MediaStreamTrack => !!t && t.kind === 'audio').forEach(t => attachStream(new MediaStream([t])));
  pc.addEventListener('track', (ev) => { if (ev.track.kind !== 'audio') return; attachStream(ev.streams[0] || new MediaStream([ev.track])); });

  const statsInt = window.setInterval(() => {
    pc.getReceivers().map(r => r.track).filter((t): t is MediaStreamTrack => !!t && t.kind === 'audio' && t.readyState === 'live' && !attached.has(t.id)).forEach(t => attachStream(new MediaStream([t])));
  }, 5000);
  pc.addEventListener('connectionstatechange', () => { if (pc.connectionState === 'closed' || pc.connectionState === 'failed') clearInterval(statsInt); });
}

// ── Service ────────────────────────────────────────────────────
@Injectable({ providedIn: 'root' })
export class CsrCallService implements OnDestroy {
  private readonly pluginConfigService = inject(TelephonyPluginConfigService);

  // ── Reactive state ─────────────────────────────────────────
  status$          = new BehaviorSubject<CallStatus>('idle');
  sipStatus$       = new BehaviorSubject<SipStatus>('idle');
  myExt$           = new BehaviorSubject<string>('');
  muted$           = new BehaviorSubject<boolean>(false);
  seconds$         = new BehaviorSubject<number>(0);
  caller$          = new BehaviorSubject<CallerInfo>({ name: 'Unknown Caller', phone: '', cnic: '', city: '', channel: 'voice' });
  transcript$      = new BehaviorSubject<TranscriptMsg[]>([]);
  captions$        = new BehaviorSubject<CaptionSegment[]>([]);
  captionsEnabled$ = new BehaviorSubject<boolean>(false);
  captionStatus$   = new BehaviorSubject<CaptionStatus>('off');
  recording$       = new BehaviorSubject<boolean>(false);
  lastRecordingUrl$  = new BehaviorSubject<string>('');
  lastRecordingName$ = new BehaviorSubject<string>('');
  dialingTarget$          = new BehaviorSubject<{ ext: string; name: string } | null>(null);
  agents$                 = new BehaviorSubject<AgentOption[]>([]);
  extensionPool$          = new BehaviorSubject<ExtensionPoolItem[]>([]);
  directoryPool$          = new BehaviorSubject<DirectoryExtensionItem[]>([]);
  isConference$           = new BehaviorSubject<boolean>(false);
  conferenceParticipants$ = new BehaviorSubject<ConferenceParticipant[]>([]);
  incomingConferenceInfo$ = new BehaviorSubject<IncomingConferenceInfo | null>(null);
  isCoaching$             = new BehaviorSubject<boolean>(false);
  coachingAgent$          = new BehaviorSubject<string>('');
  beingCoached$           = new BehaviorSubject<boolean>(false);
  coachingSupervisor$     = new BehaviorSubject<string>('');
  isMonitoring$           = new BehaviorSubject<boolean>(false);
  monitoringAgent$        = new BehaviorSubject<string>('');
  /** Fires with the failure reason when a coach/monitor session fails backend-side. */
  assistFailed$           = new Subject<string>();

  // ── Internal refs ──────────────────────────────────────────
  private ua: JsSIPUA = null;
  private session: JsSIPSession = null;
  private stomp: StompClient = null;
  private transcriptSub: { unsubscribe: () => void } | null = null;
  private channelId = '';
  private timerInterval: ReturnType<typeof setInterval> | null = null;
  private ringTone: RingToneHandle | null = null;
  private captionCapture: CaptionCapture | null = null;
  private recordingCapture: RecordingCapture | null = null;
  private wasActive = false;
  private reconnectDeadline = 0;
  private reconnectCountdown: ReturnType<typeof setInterval> | null = null;
  private reconnectCallerNum = '';

  private get pluginConfig() {
    return this.pluginConfigService.value;
  }

  private pluginApiUrl(template: string, params: Record<string, string> = {}) {
    const path = Object.entries(params).reduce(
      (value, [key, paramValue]) => value.replaceAll(`{${key}}`, encodeURIComponent(paramValue)),
      template || '',
    );
    if (/^https?:\/\//i.test(path)) return path;
    return `${API_URL}${path.startsWith('/') ? '' : '/'}${path}`;
  }

  // ── Helpers ────────────────────────────────────────────────
  private addSystem(text: string) {
    const msg: TranscriptMsg = { type: 'system', initials: 'SY', text, time: nowTime() };
    this.transcript$.next([...this.transcript$.value, msg]);
  }

  private startTimer() {
    if (this.timerInterval) return;
    this.timerInterval = setInterval(() => this.seconds$.next(this.seconds$.value + 1), 1000);
  }

  private stopTimer() {
    if (this.timerInterval) { clearInterval(this.timerInterval); this.timerInterval = null; }
  }

  private isNetworkDrop(cause: string): boolean {
    return ['Connection Error', 'Request Timeout', 'Unavailable', 'No ACK', 'Dialog Error', 'WebRTC Error'].includes(cause);
  }

  private clearReconnect() {
    if (this.reconnectCountdown) { clearInterval(this.reconnectCountdown); this.reconnectCountdown = null; }
    this.reconnectDeadline = 0;
    this.reconnectCallerNum = '';
  }

  private startReconnectWait(callerNum: string) {
    stopRingToneHandle(this.ringTone);
    this.stopCaptionCapture();
    this.stopRecordingCapture();
    this.stopTimer();
    this.session = null;
    this.reconnectCallerNum = callerNum;
    if (this.reconnectDeadline === 0) {
      this.reconnectDeadline = Date.now() + 30000;
      this.reconnectCountdown = setInterval(() => {
        const remaining = Math.max(0, Math.round((this.reconnectDeadline - Date.now()) / 1000));
        this.seconds$.next(remaining);
        if (remaining <= 0) {
          if (this.reconnectCountdown) { clearInterval(this.reconnectCountdown); this.reconnectCountdown = null; }
          this.reconnectDeadline = 0;
          this.reconnectCallerNum = '';
          this.wasActive = false;
          this.status$.next('ended');
          this.addSystem('Reconnect window expired. Call ended.');
        }
      }, 500);
    }
    this.status$.next('reconnecting');
    this.seconds$.next(Math.max(0, Math.round((this.reconnectDeadline - Date.now()) / 1000)));
    this.addSystem(`Call dropped unexpectedly. Waiting for ${callerNum} to reconnect within 30 seconds…`);
  }

  // ── Audio collection for mixing ────────────────────────────
  private collectLocalAudioTracks(): MediaStreamTrack[] {
    const pc = this.session?.connection as RTCPeerConnection | undefined;
    return pc?.getSenders().map((s: RTCRtpSender) => s.track).filter((t): t is MediaStreamTrack => !!t && t.kind === 'audio' && t.readyState === 'live') ?? [];
  }

  private collectRemoteAudioTracks(): MediaStreamTrack[] {
    const pc = this.session?.connection as RTCPeerConnection | undefined;
    const remote = pc?.getReceivers().map((r: RTCRtpReceiver) => r.track).filter((t): t is MediaStreamTrack => !!t && t.kind === 'audio' && t.readyState === 'live') ?? [];
    const el = document.getElementById('remote-audio') as HTMLAudioElement | null;
    const elStream = el?.srcObject instanceof MediaStream ? el.srcObject : null;
    const elTracks = elStream?.getAudioTracks().filter(t => t.readyState === 'live') ?? [];
    const capStream = el && 'captureStream' in el ? (el as any).captureStream?.() : null;
    const capTracks = capStream?.getAudioTracks().filter((t: MediaStreamTrack) => t.readyState === 'live') ?? [];
    return [...remote, ...elTracks, ...capTracks];
  }

  private collectCallAudioTracks(): MediaStreamTrack[] {
    return [...this.collectLocalAudioTracks(), ...this.collectRemoteAudioTracks()];
  }

  private async waitForRecordingAudioTracks(): Promise<void> {
    const started = Date.now();
    let sawLocal = this.collectLocalAudioTracks().length > 0;
    let sawRemote = this.collectRemoteAudioTracks().length > 0;
    while ((!sawLocal || !sawRemote) && Date.now() - started < RECORDING_AUDIO_WAIT_MS) {
      await new Promise(resolve => setTimeout(resolve, 150));
      sawLocal ||= this.collectLocalAudioTracks().length > 0;
      sawRemote ||= this.collectRemoteAudioTracks().length > 0;
    }
    if (!sawLocal || !sawRemote) {
      this.addSystem(`Recording started with ${sawLocal ? 'local' : 'no local'} and ${sawRemote ? 'remote' : 'no remote'} audio tracks.`);
    }
  }

  private async createMixedAudio() {
    const Ctor = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) throw new Error('AudioContext unavailable');
    const ctx = new Ctor();
    if (ctx.state === 'suspended') await ctx.resume().catch(() => undefined);
    const dest = ctx.createMediaStreamDestination();
    const sources: MediaStreamAudioSourceNode[] = [];
    const streams: MediaStream[] = [];
    const connected = new Set<string>();

    const connect = (tracks: MediaStreamTrack[]) => {
      const live = tracks.filter(t => t.kind === 'audio' && t.readyState === 'live' && !connected.has(t.id));
      live.forEach(t => { connected.add(t.id); const s = ctx.createMediaStreamSource(new MediaStream([t])); s.connect(dest); sources.push(s); });
      return live.length;
    };

    let count = connect(this.collectCallAudioTracks());
    if (!count) {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false });
      streams.push(stream); count = connect(stream.getAudioTracks());
    }
    if (!count) throw new Error('No call audio tracks available');
    return { stream: dest.stream, audioContext: ctx, sources, streams, connectedTrackIds: connected, attachCurrentTracks: () => connect(this.collectCallAudioTracks()) };
  }

  private getLoggedInSpeakerLabel(): string {
    const ext = this.myExt$.value;
    try {
      const token = localStorage.getItem('auth-token');
      const payloadPart = token?.split('.')[1];
      if (payloadPart) {
        const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
        const padded = normalized.padEnd(normalized.length + ((4 - normalized.length % 4) % 4), '=');
        const payload = JSON.parse(atob(padded)) as Record<string, unknown>;
        const name = String(payload['name'] || payload['fullName'] || payload['sub'] || '').trim();
        if (name) return ext ? `${name} (${ext})` : name;
      }
    } catch {}
    return ext ? `${this.pluginConfig.labels.agent} ${ext}` : this.pluginConfig.labels.agent;
  }

  private getRemoteSpeakerLabel(): string {
    const caller = this.caller$.value;
    if (caller.name && caller.name !== 'Unknown Caller') return caller.name;
    if (caller.phone) return /^[12]\d{3}$/.test(caller.phone)
      ? `${this.pluginConfig.labels.internalPeer} ${caller.phone}`
      : `${this.pluginConfig.labels.caller} ${caller.phone}`;
    return this.dialingTarget$.value?.name || this.dialingTarget$.value?.ext || 'Remote';
  }

  private async createSideRecordingCapture(
    label: string,
    collectTracks: () => MediaStreamTrack[],
    allowMicFallback: boolean,
  ): Promise<SideRecordingCapture | null> {
    const Ctor = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return null;
    const ctx = new Ctor();
    if (ctx.state === 'suspended') await ctx.resume().catch(() => undefined);
    const dest = ctx.createMediaStreamDestination();
    const sources: MediaStreamAudioSourceNode[] = [];
    const streams: MediaStream[] = [];
    const connected = new Set<string>();

    const connect = (tracks: MediaStreamTrack[]) => {
      const live = tracks.filter(t => t.kind === 'audio' && t.readyState === 'live' && !connected.has(t.id));
      live.forEach(t => {
        connected.add(t.id);
        const source = ctx.createMediaStreamSource(new MediaStream([t]));
        source.connect(dest);
        sources.push(source);
      });
      return live.length;
    };

    let count = connect(collectTracks());
    if (!count && allowMicFallback) {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false });
      streams.push(stream);
      count = connect(stream.getAudioTracks());
    }

    const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'].find(t => MediaRecorder.isTypeSupported(t));
    const recorder = new MediaRecorder(dest.stream, mimeType ? { mimeType } : undefined);
    let resolveStop!: (value: SideRecordingBlob) => void;
    const stopPromise = new Promise<SideRecordingBlob>(resolve => { resolveStop = resolve; });
    const side: SideRecordingCapture = {
      label,
      recorder,
      mimeType,
      chunks: [],
      streams,
      audioContext: ctx,
      sources,
      connectedTrackIds: connected,
      stopPromise,
      resolveStop,
    };

    side.attachTimer = setInterval(() => {
      if (!this.recordingCapture?.sideCaptures.includes(side)) return;
      connect(collectTracks());
    }, 1000);

    recorder.addEventListener('dataavailable', e => { if (e.data.size) side.chunks.push(e.data); });
    recorder.addEventListener('stop', () => {
      if (side.attachTimer) clearInterval(side.attachTimer);
      const blob = new Blob(side.chunks, { type: side.mimeType || 'audio/webm' });
      side.sources.forEach(s => { try { s.disconnect(); } catch {} });
      side.streams.forEach(s => s.getTracks().forEach(t => t.stop()));
      void side.audioContext.close().catch(() => undefined);
      side.resolveStop({ label: side.label, blob });
    });
    recorder.start(1000);
    return side;
  }

  // ── Recording ──────────────────────────────────────────────
  private stopRecordingCapture() {
    const cap = this.recordingCapture; this.recordingCapture = null;
    if (!cap) return;
    if (cap.attachTimer) clearInterval(cap.attachTimer);
    cap.sideCaptures.forEach(side => {
      if (side.attachTimer) clearInterval(side.attachTimer);
      if (side.recorder.state !== 'inactive') {
        try { side.recorder.stop(); } catch {}
      }
    });
    if (cap.recorder.state !== 'inactive') cap.recorder.stop();
    this.recording$.next(false);
  }

  async startRecordingCapture() {
    if (this.recordingCapture) return;
    try {
      await this.waitForRecordingAudioTracks();
      const mixed = await this.createMixedAudio();
      const sideCaptures = (await Promise.all([
        this.createSideRecordingCapture(this.getLoggedInSpeakerLabel(), () => this.collectLocalAudioTracks(), true),
        this.createSideRecordingCapture(this.getRemoteSpeakerLabel(), () => this.collectRemoteAudioTracks(), false),
      ])).filter((capture): capture is SideRecordingCapture => !!capture);
      const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'].find(t => MediaRecorder.isTypeSupported(t));
      const recorder = new MediaRecorder(mixed.stream, mimeType ? { mimeType } : undefined);
      const cap: RecordingCapture = { recorder, mimeType, chunks: [], streams: mixed.streams, audioContext: mixed.audioContext, sources: mixed.sources, connectedTrackIds: mixed.connectedTrackIds, startedAt: Date.now(), sideCaptures };
      this.recordingCapture = cap;
      cap.attachTimer = setInterval(() => { if (this.recordingCapture !== cap) return; mixed.attachCurrentTracks(); }, 1000);
      recorder.addEventListener('dataavailable', e => { if (e.data.size) cap.chunks.push(e.data); });
      recorder.addEventListener('stop', () => { void (async () => {
        if (cap.attachTimer) clearInterval(cap.attachTimer);
        const sideBlobs = await Promise.all(cap.sideCaptures.map(side => side.stopPromise));
        const blob = new Blob(cap.chunks, { type: cap.mimeType || 'audio/webm' });
        const ext = this.myExt$.value || 'agent';
        const fileName = `${this.pluginConfig.pluginId}-agent-${ext}-${new Date(cap.startedAt).toISOString().replace(/[:.]/g, '-')}.webm`;
        const url = URL.createObjectURL(blob);
        this.lastRecordingUrl$.next(url); this.lastRecordingName$.next(fileName);
        cap.sources.forEach(s => { try { s.disconnect(); } catch {} });
        cap.streams.forEach(s => s.getTracks().forEach(t => t.stop()));
        void cap.audioContext.close().catch(() => undefined);
        this.addSystem('Recording saved in browser.');
        void this.uploadRecording(blob, fileName, cap.startedAt, sideBlobs);
      })(); });
      recorder.start(1000);
      this.recording$.next(true);
      this.addSystem('Recording started.');
    } catch (e) { this.addSystem(`Recording could not start: ${e instanceof Error ? e.message : 'unknown'}`); }
  }

  toggleRecording() {
    if (this.recording$.value) this.stopRecordingCapture();
    else void this.startRecordingCapture();
  }

  private startCallPersistence() {
    void this.startRecordingCapture();
  }

  private async uploadRecording(blob: Blob, fileName: string, startedAt: number, sideBlobs: SideRecordingBlob[] = []) {
    try {
      const channelId = this.channelId || this.session?.id || '';
      const transcriptText = await this.transcribeSeparatedRecordings(sideBlobs, blob, channelId);
      const form = new FormData();
      form.append('file', blob, fileName);
      form.append('channelId', channelId);
      form.append('officerExtension', this.myExt$.value || 'unknown');
      form.append('callerNumber', this.dialingTarget$.value?.ext ?? this.caller$.value.phone ?? '');
      form.append('calledNumber', this.dialingTarget$.value?.ext ? this.myExt$.value : '');
      form.append('direction', this.dialingTarget$.value ? 'OUTBOUND' : 'INBOUND');
      form.append('startedAtEpochMs', String(startedAt));
      form.append('durationSeconds', String(Math.max(1, Math.round((Date.now() - startedAt) / 1000))));
      if (transcriptText) form.append('transcriptText', transcriptText);
      const token = localStorage.getItem('auth-token') ?? '';
      const uploadUrl = this.pluginApiUrl(this.pluginConfig.adapter.recordingWebhookUrl);
      const res = await fetch(uploadUrl, { method: 'POST', body: form, headers: token ? { Authorization: `Bearer ${token}` } : {} });
      this.addSystem(res.ok ? 'Recording saved to database.' : `Recording saved locally; DB upload failed HTTP ${res.status}.`);
    } catch { this.addSystem('Recording saved locally; DB upload failed.'); }
  }

  private async transcribeSeparatedRecordings(sideBlobs: SideRecordingBlob[], fallbackBlob: Blob, channelId: string): Promise<string> {
    const validSides = sideBlobs.filter(side => side.blob.size > 0);
    if (!validSides.length) return this.transcribeFullRecording(fallbackBlob, channelId);
    this.captionStatus$.next('starting');
    this.addSystem('Transcribing speaker-separated call recording...');

    try {
      const allSegments: TranscriptSideSegment[] = [];
      for (const [sourceIndex, side] of validSides.entries()) {
        const segments = await this.transcribeSideRecording(side, channelId, sourceIndex + 1);
        allSegments.push(...segments);
      }
      if (!allSegments.length) return this.transcribeFullRecording(fallbackBlob, channelId);

      allSegments.sort((a, b) => {
        const startDiff = (a.startMs ?? 0) - (b.startMs ?? 0);
        // On time tie: citizen (remote, higher sourceIndex) before agent (local, lower sourceIndex)
        return startDiff || b.sourceIndex - a.sourceIndex || (a.sequenceNo ?? 0) - (b.sequenceNo ?? 0);
      });

      this.captions$.next(allSegments.slice(-120));
      this.captionStatus$.next('stopped');
      this.captionsEnabled$.next(false);
      return allSegments.map(seg => {
        const start = seg.startMs != null ? this.formatTranscriptOffset(seg.startMs) : '';
        const end = seg.endMs != null ? `-${this.formatTranscriptOffset(seg.endMs)}` : '';
        const time = start ? `[${start}${end}] ` : '';
        return `${time}${seg.speakerLabel}: ${seg.text}`;
      }).join('\n');
    } catch (e) {
      this.addSystem(`Speaker-separated transcription failed: ${e instanceof Error ? e.message : 'unknown'}`);
      return this.transcribeFullRecording(fallbackBlob, channelId);
    }
  }

  private async transcribeSideRecording(side: SideRecordingBlob, channelId: string, sourceIndex: number): Promise<TranscriptSideSegment[]> {
    if (!TW_URL || !side.blob.size) return [];
    const form = new FormData();
    const safeLabel = side.label.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || `speaker-${sourceIndex}`;
    form.append('file', side.blob, `call-${channelId || Date.now()}-${safeLabel}.webm`);
    form.append('channelId', channelId || `csr-recording-${Date.now()}-${safeLabel}`);
    form.append('sequenceNo', String(sourceIndex * 100000));
    form.append('speakerLabel', side.label);
    form.append('startMs', '0');
    form.append('language', TW_LANG);

    const res = await fetch(`${TW_URL}/transcribe/chunk`, { method: 'POST', body: form });
    if (!res.ok) throw new Error(`${side.label} worker HTTP ${res.status}`);
    const json = await res.json() as { segments?: any[]; text?: string; transcription?: string };
    const segments = json.segments?.filter((s: any) => s.text?.trim()).map((seg: any, index: number) => ({
      ...seg,
      // Whisper returns start/end in seconds; convert to ms so time-based sort works
      startMs: typeof seg.startMs === 'number' ? seg.startMs
               : typeof seg.start  === 'number' ? Math.round(seg.start  * 1000) : 0,
      endMs:   typeof seg.endMs   === 'number' ? seg.endMs
               : typeof seg.end    === 'number' ? Math.round(seg.end    * 1000) : undefined,
      sequenceNo: seg.sequenceNo ?? (sourceIndex * 100000) + index,
      speakerLabel: side.label,
      sourceIndex,
      text: (seg.text as string).trim(),
    })) ?? [];
    const text = (json.text || json.transcription || '').trim();
    if (!segments.length && text) {
      segments.push({ sequenceNo: sourceIndex * 100000, speakerLabel: side.label, sourceIndex, text, isFinal: true, startMs: 0 });
    }
    return segments;
  }

  private formatTranscriptOffset(ms: number): string {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  }

  private async transcribeFullRecording(blob: Blob, channelId: string): Promise<string> {
    if (!TW_URL || !blob.size) return '';
    try {
      this.captionStatus$.next('starting');
      this.addSystem('Transcribing full call recording...');
      const form = new FormData();
      form.append('file', blob, `call-${channelId || Date.now()}-full.webm`);
      form.append('channelId', channelId || `csr-recording-${Date.now()}`);
      form.append('sequenceNo', '1');
      form.append('speakerLabel', 'Conversation');
      form.append('startMs', '0');
      form.append('language', TW_LANG);

      const res = await fetch(`${TW_URL}/transcribe/chunk`, { method: 'POST', body: form });
      if (!res.ok) throw new Error(`Worker HTTP ${res.status}`);
      const json = await res.json() as { segments?: CaptionSegment[]; text?: string; transcription?: string };
      const segments = json.segments?.filter(s => s.text?.trim()) ?? [];
      if (segments.length) {
        this.captions$.next(segments.slice(-80));
        this.captionStatus$.next('stopped');
        this.captionsEnabled$.next(false);
        return segments.map(s => `${s.speakerLabel || 'Conversation'}: ${s.text}`).join('\n');
      }
      const text = (json.text || json.transcription || '').trim();
      if (text) {
        const segment: CaptionSegment = { sequenceNo: 1, speakerLabel: 'Conversation', text, isFinal: true };
        this.captions$.next([segment]);
        this.captionStatus$.next('stopped');
        this.captionsEnabled$.next(false);
        return `Conversation: ${text}`;
      }
      this.captionStatus$.next('stopped');
      return '';
    } catch (e) {
      this.captionStatus$.next('failed');
      this.addSystem(`Full recording transcription failed: ${e instanceof Error ? e.message : 'unknown'}`);
      return '';
    }
  }

  // ── Captions ───────────────────────────────────────────────
  private stopCaptionCapture() {
    const cap = this.captionCapture; this.captionCapture = null;
    if (!cap) return;
    if (cap.chunkTimer) clearTimeout(cap.chunkTimer);
    if (cap.remoteAttachTimer) clearInterval(cap.remoteAttachTimer);
    if (cap.recorder?.state && cap.recorder.state !== 'inactive') { try { cap.recorder.stop(); } catch {} }
    cap.sources.forEach(s => { try { s.disconnect(); } catch {} });
    cap.streams.forEach(s => s.getTracks().forEach(t => { if (t.readyState !== 'ended') t.stop(); }));
    void cap.audioContext.close().catch(() => undefined);
  }

  private subscribeCaptions(channelId: string) {
    this.transcriptSub?.unsubscribe(); this.transcriptSub = null;
    if (!this.stomp?.connected || !channelId) return;
    this.transcriptSub = this.stomp.subscribe(`/topic/calls/${channelId}/transcript`, (msg: { body: string }) => {
      try {
        const ev = JSON.parse(msg.body) as any;
        if (ev.type === 'status' && ev.status) {
          const s = String(ev.status).toLowerCase() as CaptionStatus;
          this.captionStatus$.next(s);
          this.captionsEnabled$.next(s === 'starting' || s === 'live');
        } else if (ev.type === 'segment' && ev.text?.trim()) {
          this.captionStatus$.next('live'); this.captionsEnabled$.next(true);
          const seg: CaptionSegment = { sequenceNo: ev.sequenceNo, speakerLabel: ev.speakerLabel, text: ev.text, isFinal: ev.isFinal, startMs: ev.startMs, endMs: ev.endMs };
          const current = this.captions$.value;
          if (ev.sequenceNo != null) {
            const idx = current.findIndex(c => c.sequenceNo === ev.sequenceNo);
            if (idx >= 0) { const next = [...current]; next[idx] = seg; this.captions$.next(next.slice(-80)); return; }
          }
          this.captions$.next([...current, seg].slice(-80));
        }
      } catch {}
    });
  }

  async toggleCaptions() {
    this.stopCaptionCapture();
    this.transcriptSub?.unsubscribe();
    this.transcriptSub = null;
    this.captionsEnabled$.next(false);
    this.captionStatus$.next(this.captions$.value.length ? 'stopped' : 'off');
    this.addSystem('Live caption chunks are disabled. The full call transcript is generated after the call ends.');
  }

  private async startCaptionCapture(channelId: string): Promise<boolean> {
    this.stopCaptionCapture();
    if (!TW_URL) { this.captionStatus$.next('failed'); return false; }
    try {
      const Ctor = window.AudioContext || (window as any).webkitAudioContext;
      if (!Ctor) throw new Error('AudioContext unavailable');
      const ctx = new Ctor();
      if (ctx.state === 'suspended') await ctx.resume().catch(() => undefined);
      const dest = ctx.createMediaStreamDestination();
      const sources: MediaStreamAudioSourceNode[] = [];
      const connected = new Set<string>();
      const streams: MediaStream[] = [];

      const connectTracks = (tracks: MediaStreamTrack[]) => {
        const live = tracks.filter(t => t.kind === 'audio' && t.readyState === 'live' && !connected.has(t.id));
        if (!live.length) return 0;
        const src = ctx.createMediaStreamSource(new MediaStream(live));
        live.forEach(t => connected.add(t.id)); src.connect(dest); sources.push(src); return live.length;
      };

      const remoteTracks = () => {
        const pc = this.session?.connection as RTCPeerConnection | undefined;
        const rec = pc?.getReceivers().map((r: RTCRtpReceiver) => r.track).filter((t): t is MediaStreamTrack => !!t && t.kind === 'audio' && t.readyState === 'live') ?? [];
        const el = document.getElementById('remote-audio') as HTMLAudioElement | null;
        const elStream = el?.srcObject instanceof MediaStream ? el.srcObject : null;
        const elTracks = elStream?.getAudioTracks().filter(t => t.readyState === 'live') ?? [];
        return [...rec, ...elTracks];
      };
      const localTracks = () => { const pc = this.session?.connection as RTCPeerConnection | undefined; return pc?.getSenders().map((s: RTCRtpSender) => s.track).filter((t): t is MediaStreamTrack => !!t && t.kind === 'audio' && t.readyState === 'live') ?? []; };

      let lc = connectTracks(localTracks());
      if (!lc) {
        const s = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false });
        streams.push(s); lc = connectTracks(s.getAudioTracks());
      }
      connectTracks(remoteTracks());

      const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'].find(t => MediaRecorder.isTypeSupported(t));
      const cap: CaptionCapture = { recorder: null, mediaStream: dest.stream, mimeType, streams, audioContext: ctx, sources, connectedTrackIds: connected, sequenceNo: 1, startedAt: Date.now() };
      this.captionCapture = cap;

      cap.remoteAttachTimer = setInterval(() => { if (this.captionCapture !== cap) return; connectTracks(remoteTracks()); }, 1000);

      const uploadChunk = async (blob: Blob) => {
        if (!blob.size || this.captionCapture !== cap) return;
        const seq = cap.sequenceNo++; const form = new FormData();
        form.append('file', blob, `call-${channelId}-${seq}.webm`);
        form.append('channelId', channelId); form.append('sequenceNo', String(seq));
        form.append('speakerLabel', this.caller$.value.name && this.caller$.value.name !== 'Unknown Caller' ? this.caller$.value.name : 'Caller');
        form.append('startMs', String(Date.now() - cap.startedAt)); form.append('language', TW_LANG);
        try {
          const res = await fetch(`${TW_URL}/transcribe/chunk`, { method: 'POST', body: form });
          if (!res.ok) throw new Error(`Worker HTTP ${res.status}`);
          const json = await res.json() as { segments?: CaptionSegment[] };
          const segs = json.segments?.filter(s => s.text?.trim()) ?? [];
          if (!segs.length) return;
          this.captionStatus$.next('live'); this.captionsEnabled$.next(true);
          const cur = this.captions$.value; const next = [...cur];
          segs.forEach(seg => { const idx = seg.sequenceNo != null ? next.findIndex(c => c.sequenceNo === seg.sequenceNo) : -1; if (idx >= 0) next[idx] = seg; else next.push(seg); });
          this.captions$.next(next.slice(-80));
        } catch { this.captionStatus$.next('live'); }
      };

      const startChunk = () => {
        if (this.captionCapture !== cap) return;
        const rec = new MediaRecorder(cap.mediaStream, cap.mimeType ? { mimeType: cap.mimeType } : undefined);
        const parts: Blob[] = []; cap.recorder = rec;
        rec.addEventListener('dataavailable', e => { if (e.data.size) parts.push(e.data); });
        rec.addEventListener('stop', () => { const chunk = parts.length ? new Blob(parts, { type: cap.mimeType || 'audio/webm' }) : null; void (async () => { if (chunk) await uploadChunk(chunk); if (this.captionCapture === cap) cap.chunkTimer = setTimeout(startChunk, 0); })(); });
        rec.addEventListener('error', () => { if (parts.length) void uploadChunk(new Blob(parts, { type: cap.mimeType || 'audio/webm' })); if (this.captionCapture === cap) cap.chunkTimer = setTimeout(startChunk, 0); });
        rec.start();
        cap.chunkTimer = setTimeout(() => { if (this.captionCapture === cap && rec.state === 'recording') rec.stop(); }, CAPTION_CHUNK_MS);
      };

      startChunk();
      return true;
    } catch (e) {
      this.stopCaptionCapture(); this.captionsEnabled$.next(false); this.captionStatus$.next('failed');
      this.addSystem(`Captions failed: ${e instanceof Error ? e.message : 'unknown'}`);
      return false;
    }
  }

  // ── CTI screen pop ─────────────────────────────────────────
  private async fetchCti(phone: string, knownName?: string) {
    // Internal extensions — resolve name from agents list, no DB lookup needed
    if (/^[12]\d{3}$/.test(phone)) {
      const agent = this.agents$.value.find(a => a.ext === phone);
      const name = agent?.name ?? knownName ?? `Ext ${phone}`;
      this.caller$.next({ name, phone, cnic: '—', city: '—', channel: 'voice' });
      return;
    }
    try {
      const token = localStorage.getItem('auth-token') ?? '';
      const lookupUrl = this.pluginApiUrl(this.pluginConfig.adapter.callerLookupUrl, { phone });
      const res = await fetch(lookupUrl, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) return;
      const json: any = await res.json();
      const data = json?.data ?? {};
      const businessRecords: Array<Record<string, string>> = data?.businessRecords ?? data?.complaints ?? [];
      const first = businessRecords[0];
      const ivrOption = data?.ivrOption ? String(data.ivrOption) : undefined;
      this.caller$.next({
        name: first?.['complainantName'] ?? data?.callerName ?? knownName ?? 'Unknown Caller',
        phone,
        cnic: first?.['cnic'] ?? '—',
        city: '—',
        channel: 'voice',
        callerLanguage: data?.callerLanguage ? String(data.callerLanguage) : undefined,
        ivrOption,
        ivrOptionLabel: ivrOption ? this.ivrOptionLabel(ivrOption) : undefined,
      });
    } catch {}
  }

  /**
   * Terminates the local jsSIP session if it is a monitor/coach (9001/9002) call.
   * Called on Stop so the browser leg always dies even when the backend stop
   * endpoint cannot clean up (e.g. the monitored call already ended) — otherwise
   * the supervisor channel leaks and stays up on Asterisk indefinitely.
   */
  terminateAssistSession(): void {
    const s = this.session;
    if (!s) return;
    const num: string = s.remote_identity?.uri?.user ?? '';
    if (num !== '9001' && num !== '9002') return;
    try { s.terminate(); } catch {}
    this.session = null;
    this.status$.next('idle');
    this.stopTimer();
  }

  private detectSupervisorAssistKind(displayName: string, callerNumber: string, session: JsSIPSession): SupervisorAssistKind {
    const parts = [displayName, callerNumber];
    try {
      const headers = (session as any)?.request?.headers;
      if (headers) parts.push(JSON.stringify(headers));
    } catch {}

    const marker = parts.join(' ').toLowerCase();
    if (callerNumber === '9001' || marker.includes('pulse monitor')) return 'monitor';
    if (callerNumber === '9002' || marker.includes('pulse coach') || marker.includes('whisper-sup')) return 'coach';
    return '';
  }

  private ivrOptionLabel(option: string): string {
    switch (option) {
      case '0': return 'Live agent';
      case '1': return 'Complaint registration';
      case '2': return 'Complaint status check';
      case '3': return 'Appeal';
      case '4': return 'Drop complaint';
      case '5': return 'Resolved complaint feedback';
      default: return `Option ${option}`;
    }
  }

  private dtoToAgentOption(dto: AgentAvailabilityDto): AgentOption {
    const status: AgentOption['status'] =
      dto.status === 'ON_CALL' ? 'On Call' : dto.status === 'AVAILABLE' ? 'Available' : 'Away';
    const color = dto.status === 'AVAILABLE' ? '#00c48c' : dto.status === 'ON_CALL' ? '#ff5b5b' : '#ffb020';
    return { name: dto.fullName, ext: dto.sipExtension, status, color };
  }

  async fetchExtensionPool() {
    try {
      const token = localStorage.getItem('auth-token') ?? '';
      const res = await fetch(`${API_URL}/api/v1/telephony/extensions/available`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) return;
      const json: any = await res.json();
      const list: ExtensionPoolItem[] = json?.data ?? json ?? [];
      this.extensionPool$.next(list);
    } catch {}
  }

  async fetchDirectoryPool(): Promise<void> {
    try {
      const token = localStorage.getItem('auth-token') ?? '';
      const res = await fetch(`${API_URL}/api/v1/telephony/extensions/directory`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) return;
      const json: any = await res.json();
      const list: DirectoryExtensionItem[] = json?.data ?? json ?? [];
      this.directoryPool$.next(list);
    } catch {}
  }

  async fetchAgentsPublic(myExt: string) { return this.fetchAgents(myExt); }

  private async fetchAgents(myExt: string) {
    try {
      const token = localStorage.getItem('auth-token') ?? '';
      const res = await fetch(`${API_URL}/api/v1/telephony/calls/agents/availability`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) return;
      const json: any = await res.json();
      const list: AgentAvailabilityDto[] = json?.data ?? json ?? [];
      this.agents$.next(
        list.filter(a => a.sipExtension !== myExt).map(a => this.dtoToAgentOption(a))
      );
    } catch {}
  }

  async reserveAndSelect(extensionNumber: string) {
    const token = localStorage.getItem('auth-token') ?? '';
    try {
      const res = await fetch(`${API_URL}/api/v1/telephony/extensions/${encodeURIComponent(extensionNumber)}/reserve`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const err: any = await res.json().catch(() => ({}));
        this.addSystem(`Could not reserve ext ${extensionNumber}: ${err?.message ?? res.status}`);
        void this.fetchExtensionPool();
        return;
      }
      const json: any = await res.json();
      const pass: string = json?.data?.password ?? '';
      void this.fetchExtensionPool();
      this.selectExtension(extensionNumber, pass);
    } catch (e) {
      this.addSystem(`Extension reservation failed: ${e instanceof Error ? e.message : 'network error'}`);
    }
  }

  // ── Extension selection ────────────────────────────────────
  selectExtension(ext: string, pass: string) {
    this.ua?.stop(); this.stomp?.deactivate();
    stopRingToneHandle(this.ringTone); this.stopCaptionCapture(); this.stopRecordingCapture();
    this.ua = null; this.session = null;
    this.myExt$.next(ext); this.sipStatus$.next('idle');

    if (!ext) {
      const keys = this.pluginConfig.storageKeys;
      const hadExt = localStorage.getItem(keys.sipExtension) ?? localStorage.getItem(keys.legacySipExtension);
      localStorage.removeItem(keys.sipExtension);
      localStorage.removeItem(keys.sipPassword);
      localStorage.removeItem(keys.legacySipExtension);
      localStorage.removeItem(keys.legacySipPassword);
      if (hadExt) {
        const token = localStorage.getItem('auth-token') ?? '';
        fetch(`${API_URL}/api/v1/telephony/extensions/release`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }).catch(() => undefined);
      }
      void this.fetchExtensionPool();
      return;
    }

    localStorage.setItem(this.pluginConfig.storageKeys.sipExtension, ext);
    localStorage.setItem(this.pluginConfig.storageKeys.sipPassword, pass);
    this.ringTone ??= createRingToneHandle();

    if (!SIP_WS_URL) { this.sipStatus$.next('registered'); this.addSystem(`[Mock] Registered as Ext ${ext}`); return; }

    this.sipStatus$.next('connecting'); this.addSystem(`Connecting as Ext ${ext}…`);

    // JsSIP
    import('jssip').then(mod => {
      const JsSIP = (mod as any).default ?? mod;
      const socket = new JsSIP.WebSocketInterface(SIP_WS_URL);
      const ua: JsSIPUA = new JsSIP.UA({ sockets: [socket], uri: `sip:${ext}@${SIP_DOMAIN}`, password: pass, no_answer_timeout: 3600, session_timers: false, register: true });

      ua.on('registered',        () => { this.sipStatus$.next('registered'); this.addSystem(`Ext ${ext} registered — ready for calls.`); });
      ua.on('unregistered',      () => this.sipStatus$.next('idle'));
      ua.on('registrationFailed',() => { this.sipStatus$.next('failed'); this.addSystem('SIP registration failed.'); });
      ua.on('disconnected',      () => { this.sipStatus$.next('failed'); this.addSystem('SIP disconnected.'); });

      ua.on('newRTCSession', (data: { session: JsSIPSession }) => {
        const session = data.session;
        const munger = createSdpMunger();
        if (session.direction !== 'incoming') return;
        this.session = session;

        session.on('icecandidate', ({ candidate, ready }: { candidate: RTCIceCandidate; ready?: () => void }) => {
          if (!candidate) return;
          munger.rememberCandidate(candidate);
          if (munger.shouldSignalReady(candidate)) ready?.();
        });
        session.on('sdp', (ev: any) => munger.munge(ev));
        if (session.connection) attachRemoteAudio(session.connection, t => this.addSystem(t));
        session.on('peerconnection', ({ peerconnection }: { peerconnection: RTCPeerConnection }) => attachRemoteAudio(peerconnection, t => this.addSystem(t)));

        const callerNum: string = session.remote_identity?.uri?.user ?? 'unknown';
        const callerDisplayName: string = session.remote_identity?.display_name ?? '';
        const inReconnectWindow = this.reconnectDeadline > 0 && Date.now() < this.reconnectDeadline;

        // Set caller identity immediately from SIP headers — CTI may refine it
        this.caller$.next({
          name: callerDisplayName || callerNum,
          phone: callerNum,
          cnic: '—', city: '—', channel: 'voice',
        });

        const wireSessionHandlers = (autoAnswered: boolean) => {
          session.on('confirmed', () => {
            stopRingToneHandle(this.ringTone);
            session.unmute?.({ audio: true });
            const sender = (session.connection as RTCPeerConnection | undefined)?.getSenders().find((s: RTCRtpSender) => s.track?.kind === 'audio');
            if (sender?.track) sender.track.enabled = true;
            this.wasActive = true;
            this.clearReconnect();
            if (!autoAnswered) { this.status$.next('active'); this.seconds$.next(0); this.startTimer(); this.startCallPersistence(); }
          });
          session.on('ended', (e: any) => {
            const cause: string = e?.cause ?? '';
            stopRingToneHandle(this.ringTone); this.stopCaptionCapture(); this.stopRecordingCapture(); this.stopTimer();
            if (this.wasActive && this.isNetworkDrop(cause)) { this.startReconnectWait(callerNum); return; }
            this.wasActive = false; this.clearReconnect();
            this.status$.next('ended'); this.addSystem('Call ended.');
          });
          session.on('failed', (e: any) => {
            const cause: string = e?.cause ?? '';
            stopRingToneHandle(this.ringTone); this.stopCaptionCapture(); this.stopRecordingCapture(); this.stopTimer();
            if (this.wasActive && this.isNetworkDrop(cause)) { this.startReconnectWait(callerNum); return; }
            this.wasActive = false; this.clearReconnect();
            this.status$.next('idle'); this.addSystem('Call failed / missed.');
          });
        };

        const supervisorAssistKind = this.detectSupervisorAssistKind(callerDisplayName, callerNum, session);
        if (supervisorAssistKind) {
          const label = supervisorAssistKind === 'monitor' ? 'PULSE Monitor' : 'PULSE Coach';
          this.caller$.next({ name: label, phone: callerNum, cnic: '-', city: '-', channel: 'voice' });
          this.dialingTarget$.next(null);
          stopRingToneHandle(this.ringTone);
          this.addSystem(`${label} session connected silently.`);
          wireSessionHandlers(true);
          setTimeout(() => {
            try {
              session.answer({ mediaConstraints: { audio: true, video: false }, pcConfig: PC_CONFIG });
              this.status$.next('active');
              this.seconds$.next(0);
              this.startTimer();
            } catch (e) {
              this.status$.next('idle');
              this.addSystem(`${label} failed to answer: ${e instanceof Error ? e.message : 'unknown error'}`);
            }
          }, 100);
          return;
        }

        if (inReconnectWindow) {
          if (callerNum === this.reconnectCallerNum) {
            this.clearReconnect();
            this.dialingTarget$.next(null);
            void this.fetchCti(callerNum, callerDisplayName);
            this.addSystem(`Reconnect call from ${callerNum} — auto-answering.`);
            wireSessionHandlers(true);
            setTimeout(() => {
              session.answer({ mediaConstraints: { audio: true, video: false }, pcConfig: PC_CONFIG });
              this.status$.next('active'); this.seconds$.next(0); this.startTimer();
              this.startCallPersistence();
            }, 300);
          } else {
            // Different caller — reject as busy so Asterisk reroutes them
            try { session.terminate({ status_code: 486, reason_phrase: 'Busy Here' }); } catch {}
            this.addSystem(`Busy: rejected call from ${callerNum} during reconnect window.`);
          }
          return;
        }

        this.dialingTarget$.next(null);
        this.status$.next('ringing');
        playRingToneHandle(this.ringTone);
        void this.fetchCti(callerNum, callerDisplayName);
        this.addSystem(`Incoming call from ${callerDisplayName || callerNum}`);
        wireSessionHandlers(false);
      });

      ua.start(); this.ua = ua;
    });

    // STOMP
    if (STOMP_URL) {
      import('@stomp/stompjs').then(mod => {
        const Client = mod.Client ?? (mod as any).default?.Client ?? (mod as any).default;
        const client: StompClient = new Client({
          brokerURL: stompBrokerUrl(STOMP_URL),
          reconnectDelay: 5000,
          onConnect: () => {
            client.subscribe(`/topic/calls/${ext}`, (msg: { body: string }) => {
              try {
                const ev = JSON.parse(msg.body) as any;
                const channelId = ev['channelId'] as string | undefined;
                const callerNumber = ev['callerNumber'] as string | undefined;
                const evStatus = ev['status'] as string | undefined;

                if (evStatus === 'CONFERENCE_RINGING') {
                  this.incomingConferenceInfo$.next({
                    channelId: channelId ?? '',
                    callerNumber: callerNumber ?? '',
                    initiatorExt: ev['initiatorExt'] ?? '',
                    initiatorName: ev['initiatorName'] ?? '',
                  });
                  if (channelId) this.channelId = channelId;
                  if (callerNumber) void this.fetchCti(callerNumber);
                  return;
                }

                if (evStatus === 'CONFERENCE_JOINED') {
                  this.isConference$.next(true);
                  const participants: ConferenceParticipant[] = (ev['participants'] as any[] ?? []).map((p: any) => ({
                    ext: p.ext ?? '', name: p.name ?? '', role: p.role ?? 'CONFERENCE_AGENT',
                  }));
                  this.conferenceParticipants$.next(participants);
                  this.incomingConferenceInfo$.next(null);
                  if (channelId) { this.channelId = channelId; if (this.captionsEnabled$.value) this.subscribeCaptions(channelId); }
                  if (callerNumber) void this.fetchCti(callerNumber);
                  return;
                }

                if (evStatus === 'CONFERENCE_LEFT') {
                  const participants: ConferenceParticipant[] = (ev['participants'] as any[] ?? []).map((p: any) => ({
                    ext: p.ext ?? '', name: p.name ?? '', role: p.role ?? 'CONFERENCE_AGENT',
                  }));
                  this.conferenceParticipants$.next(participants);
                  if (participants.filter(p => p.role !== 'CITIZEN').length <= 1) {
                    this.isConference$.next(false);
                  }
                  return;
                }

                if (evStatus === 'WHISPER_ACTIVE') {
                  this.isCoaching$.next(true);
                  this.coachingAgent$.next(ev['agentExt'] ?? '');
                  return;
                }

                if (evStatus === 'SUPERVISOR_ASSIST_PROGRESS') {
                  const mode = ev['assistMode'] ? String(ev['assistMode']).replace(/_/g, ' ').toLowerCase() : 'supervisor assist';
                  const state = ev['assistState'] ? String(ev['assistState']).replace(/_/g, ' ').toLowerCase() : 'progress';
                  this.addSystem(ev['message'] ?? `${mode}: ${state}`);
                  return;
                }

                if (evStatus === 'SUPERVISOR_ASSIST_FAILED') {
                  const failMsg = ev['message'] ?? 'Supervisor assist failed. Please retry.';
                  this.addSystem(failMsg);
                  this.assistFailed$.next(failMsg);
                  return;
                }

                if (evStatus === 'WHISPER_COACHING') {
                  this.beingCoached$.next(true);
                  this.coachingSupervisor$.next(ev['supervisorExt'] ?? '');
                  this.addSystem('A supervisor is coaching you privately. The caller cannot hear them.');
                  return;
                }

                if (evStatus === 'WHISPER_ENDED') {
                  this.isCoaching$.next(false);
                  this.coachingAgent$.next('');
                  this.beingCoached$.next(false);
                  this.coachingSupervisor$.next('');
                  return;
                }

                if (evStatus === 'MONITOR_ACTIVE') {
                  this.isMonitoring$.next(true);
                  this.monitoringAgent$.next(ev['agentExt'] ?? '');
                  return;
                }

                if (evStatus === 'MONITOR_ENDED') {
                  this.isMonitoring$.next(false);
                  this.monitoringAgent$.next('');
                  return;
                }

                if (channelId) { this.channelId = channelId; if (this.captionsEnabled$.value) this.subscribeCaptions(channelId); }
                if (callerNumber) void this.fetchCti(callerNumber);
                if (evStatus === 'COMPLETED' || evStatus === 'MISSED') this.status$.next('ended');
              } catch {}
            });

            // Live agent availability updates
            client.subscribe('/topic/agents', (msg: { body: string }) => {
              try {
                const updated = JSON.parse(msg.body) as AgentAvailabilityDto;
                if (updated.sipExtension === ext) return; // skip self
                const current = this.agents$.value;
                if (updated.status === 'OFFLINE') {
                  this.agents$.next(current.filter(a => a.ext !== updated.sipExtension));
                  return;
                }
                const mapped = this.dtoToAgentOption(updated);
                const idx = current.findIndex(a => a.ext === mapped.ext);
                if (idx >= 0) {
                  const next = [...current]; next[idx] = mapped; this.agents$.next(next);
                } else {
                  this.agents$.next([...current, mapped]);
                }
              } catch {}
            });

            // Initial agent list fetch
            void this.fetchAgents(ext);

            // Resubscribe to transcript channel if captions were active before STOMP reconnected
            if (this.captionsEnabled$.value && this.channelId) {
              this.subscribeCaptions(this.channelId);
            }
          },
        });
        client.activate(); this.stomp = client;
      });
    }
  }

  // ── Call controls ──────────────────────────────────────────
  answerCall() {
    if (this.session) {
      stopRingToneHandle(this.ringTone);
      this.session.answer({ mediaConstraints: { audio: true, video: false }, pcConfig: PC_CONFIG });
      this.status$.next('active'); this.seconds$.next(0); this.startTimer();
      this.addSystem('Connecting…');
      this.startCallPersistence();
    } else {
      this.status$.next('active'); this.seconds$.next(0); this.startTimer();
      this.addSystem('Call answered.');
    }
  }

  holdCall() { if (this.session) this.session.hold(); this.status$.next('on-hold'); this.addSystem('Call placed on hold.'); }
  resumeCall() { if (this.session) this.session.unhold(); this.status$.next('active'); this.addSystem('Call resumed.'); }

  muteCall()   { if (this.session) this.session.mute({ audio: true });   this.muted$.next(true);  this.addSystem('Microphone muted.'); }
  unmuteCall() { if (this.session) this.session.unmute({ audio: true }); this.muted$.next(false); this.addSystem('Microphone unmuted.'); }

  endCall() {
    this.wasActive = false;
    this.clearReconnect();
    stopRingToneHandle(this.ringTone); this.stopCaptionCapture(); this.stopRecordingCapture(); this.stopTimer();
    const s = this.seconds$.value;
    const dur = `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
    if (this.session) { try { this.session.terminate(); } catch {} }
    this.session = null;
    this.isConference$.next(false); this.conferenceParticipants$.next([]); this.incomingConferenceInfo$.next(null);
    this.isCoaching$.next(false); this.coachingAgent$.next(''); this.beingCoached$.next(false); this.coachingSupervisor$.next('');
    this.isMonitoring$.next(false); this.monitoringAgent$.next('');
    this.status$.next('ended'); this.addSystem(`Call ended · Duration: ${dur}`);
  }

  transferCall(agentExt: string, agentName: string) {
    if (!this.channelId) { this.addSystem('Transfer failed: no channel ID.'); return; }
    this.stopCaptionCapture(); this.stopRecordingCapture(); this.stopTimer();
    this.addSystem(`Transferring call to ${agentName} (Ext ${agentExt})…`);
    this.status$.next('ended');
    // Use backend ARI transfer instead of session.refer(). session.refer() terminates the
    // old SIP session which triggers ChannelHangupRequest and disconnects the citizen.
    // The backend removes old agent channel mappings before hanging up, making that event a no-op.
    // The backend then plays MOH on the citizen and originates the new agent.
    // The old agent's SIP session is terminated by Asterisk via ARI hangup — no need to call
    // session.terminate() here; the JsSIP 'ended' event fires naturally when Asterisk sends BYE.
    const token = localStorage.getItem('auth-token') ?? '';
    fetch(`${API_URL}/api/v1/telephony/calls/${encodeURIComponent(this.channelId)}/transfer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ targetExt: agentExt }),
    })
      .then(r => { if (!r.ok) this.addSystem(`Transfer API error: HTTP ${r.status}.`); })
      .catch(() => this.addSystem('Transfer request failed.'));
  }

  startConference(targetExt: string, targetName = targetExt) {
    if (!this.session || this.status$.value !== 'active') return;
    const channelId = this.channelId;
    if (!channelId) { this.addSystem('Conference unavailable: no channel ID.'); return; }
    // Do NOT hold agent 1 — they stay active in the bridge while agent 2 is being rung.
    // The backend reuses the existing bridge so all three parties share one mixing bridge.
    this.addSystem(`Ringing ${targetName} (Ext ${targetExt}) for conference…`);
    const token = localStorage.getItem('auth-token') ?? '';
    fetch(`${API_URL}/api/v1/telephony/calls/conference`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ channelId, targetExt }),
    })
      .then(r => r.json())
      .then((json: any) => {
        const data = json?.data ?? json;
        if (data?.status === 'conference_ringing') {
          this.addSystem(`${targetName} is ringing. Call will connect when they answer.`);
        } else {
          this.addSystem('Conference setup failed.');
        }
      })
      .catch(() => this.addSystem('Conference request failed.'));
  }

  stopWhisperCoach(agentExt: string) {
    const token = localStorage.getItem('auth-token') ?? '';
    fetch(`${API_URL}/api/v1/supervisor/calls/whisper/${encodeURIComponent(agentExt)}`, {
      method: 'DELETE',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).catch(() => undefined);
    this.isCoaching$.next(false);
    this.coachingAgent$.next('');
  }

  makeCall(ext: string, name = ext) {
    if (!this.ua) { this.dialingTarget$.next({ ext, name }); this.status$.next('active'); this.seconds$.next(0); this.startTimer(); this.addSystem(`[Mock] Connected to ${name} (Ext ${ext})`); return; }
    this.dialingTarget$.next({ ext, name });
    this.status$.next('ringing');
    playRingToneHandle(this.ringTone);
    this.addSystem(`Dialing ${name} (Ext ${ext})…`);

    const outSession = this.ua.call(`sip:${ext}@${SIP_DOMAIN}`, { mediaConstraints: { audio: true, video: false }, pcConfig: PC_CONFIG });
    const munger = createSdpMunger();
    this.session = outSession;

    outSession.on('icecandidate', ({ candidate, ready }: { candidate: RTCIceCandidate; ready?: () => void }) => { if (!candidate) return; munger.rememberCandidate(candidate); if (munger.shouldSignalReady(candidate)) ready?.(); });
    outSession.on('sdp', (ev: any) => munger.munge(ev));
    if (outSession.connection) attachRemoteAudio(outSession.connection, t => this.addSystem(t));
    outSession.on('peerconnection', ({ peerconnection }: { peerconnection: RTCPeerConnection }) => attachRemoteAudio(peerconnection, t => this.addSystem(t)));
    outSession.on('confirmed',  () => { stopRingToneHandle(this.ringTone); this.status$.next('active'); this.seconds$.next(0); this.startTimer(); this.addSystem(`Call connected to ${name}.`); this.startCallPersistence(); });
    outSession.on('failed',     () => { stopRingToneHandle(this.ringTone); this.stopCaptionCapture(); this.stopRecordingCapture(); this.stopTimer(); this.status$.next('idle'); this.dialingTarget$.next(null); this.addSystem(`Call to ${name} failed.`); });
    outSession.on('ended',      () => { stopRingToneHandle(this.ringTone); this.stopCaptionCapture(); this.stopRecordingCapture(); this.stopTimer(); this.status$.next('ended'); this.addSystem('Call ended.'); });
  }

  sendMessage(text: string) {
    if (!text.trim() || this.status$.value !== 'active') return;
    this.transcript$.next([...this.transcript$.value, { type: 'agent', initials: 'SA', text, time: nowTime() }]);
  }

  saveDraftNote(note: string, details: Partial<WrapUpDetails> = {}) {
    if (!this.channelId || !note.trim()) return;
    const token = localStorage.getItem('auth-token') ?? '';
    const wrapUpUrl = this.pluginApiUrl(this.pluginConfig.adapter.wrapUpUrl, { channelId: this.channelId });
    fetch(wrapUpUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(this.wrapUpPayload({ ...details, notes: note })),
    }).catch(() => undefined);
  }

  async saveWrapUp(details: WrapUpDetails) {
    this.wasActive = false;
    this.clearReconnect();
    if (this.channelId) {
      try {
        const token = localStorage.getItem('auth-token') ?? '';
        const wrapUpUrl = this.pluginApiUrl(this.pluginConfig.adapter.wrapUpUrl, { channelId: this.channelId });
        await fetch(wrapUpUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify(this.wrapUpPayload(details)) });
      } catch {}
    }
    this.status$.next('idle'); this.seconds$.next(0); this.muted$.next(false);
    stopRingToneHandle(this.ringTone); this.stopCaptionCapture(); this.stopRecordingCapture(); this.stopTimer();
    this.transcriptSub?.unsubscribe(); this.transcriptSub = null;
    this.captions$.next([]); this.captionsEnabled$.next(false); this.captionStatus$.next('off');
    this.transcript$.next([]); this.dialingTarget$.next(null);
    this.isConference$.next(false); this.conferenceParticipants$.next([]); this.incomingConferenceInfo$.next(null);
    this.isCoaching$.next(false); this.coachingAgent$.next(''); this.beingCoached$.next(false); this.coachingSupervisor$.next('');
    this.isMonitoring$.next(false); this.monitoringAgent$.next('');
    this.channelId = ''; this.session = null;
  }

  private wrapUpPayload(details: WrapUpDetails) {
    return {
      notes: details.notes,
      businessRecordId: null,
      businessRecordType: null,
      complaintId: null,
      disposition: details.disposition || null,
      callReason: details.callReason || null,
      sourceOfInformation: details.sourceOfInformation || null,
      callbackRequired: !!details.callbackRequired,
      callbackDueAt: details.callbackRequired && details.callbackDueAt ? details.callbackDueAt : null,
      callbackStatus: details.callbackRequired ? (details.callbackStatus || 'PENDING') : null,
      holdDurationSeconds: details.holdDurationSeconds ?? null,
    };
  }

  destroy() {
    this.wasActive = false;
    this.clearReconnect();
    this.stopCaptionCapture(); this.stopRecordingCapture(); this.stopTimer();
    stopRingToneHandle(this.ringTone);
    try { this.session?.terminate(); } catch {}
    this.ua?.stop(); this.stomp?.deactivate();
    this.ua = null; this.session = null; this.stomp = null;
  }

  ngOnDestroy() { this.destroy(); }
}
